import { HolderOutlined } from '@ant-design/icons';
import { Button, Checkbox, Popover, Tooltip } from 'antd';
import {
	ColumnPreference,
	COLUMN_LABELS,
	normalizeColumnPreferences,
	preparePatchColumnPreferences,
} from 'constants/payrollOverviewColumns';
import { toastText } from 'utils/utils';
import { useEffect, useState } from 'react';
import { FaSlidersH } from 'react-icons/fa';
import styles from './index.module.scss';

type PopoverListItem =
	| { type: 'fixed'; key: 'payPeriod' | 'action' }
	| { type: 'custom'; key: ColumnPreference['key']; visible: boolean };

type Props = {
	columnPreferences: ColumnPreference[];
	onApply: (columns: ColumnPreference[]) => Promise<void>;
	isApplying?: boolean;
};

const buildPopoverList = (columns: ColumnPreference[]): PopoverListItem[] => [
	{ type: 'fixed', key: 'payPeriod' },
	...columns.map((column) => ({
		type: 'custom' as const,
		key: column.key,
		visible: column.visible,
	})),
	{ type: 'fixed', key: 'action' },
];

const ColumnSelector = ({
	columnPreferences,
	onApply,
	isApplying = false,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [draftColumns, setDraftColumns] = useState<ColumnPreference[]>(
		normalizeColumnPreferences(columnPreferences)
	);
	const [draggedKey, setDraggedKey] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setDraftColumns(normalizeColumnPreferences(columnPreferences));
		}
	}, [open, columnPreferences]);

	const popoverList = buildPopoverList(draftColumns);

	const handleVisibilityChange = (key: ColumnPreference['key'], visible: boolean) => {
		setDraftColumns((prev) =>
			prev.map((column) =>
				column.key === key ? { ...column, visible } : column
			)
		);
	};

	const handleDragStart = (key: ColumnPreference['key']) => {
		setDraggedKey(key);
	};

	const handleDragOver = (
		event: React.DragEvent<HTMLDivElement>,
		targetKey: ColumnPreference['key']
	) => {
		event.preventDefault();
		if (!draggedKey || draggedKey === targetKey) {
			return;
		}

		setDraftColumns((prev) => {
			const draggedIndex = prev.findIndex((column) => column.key === draggedKey);
			const targetIndex = prev.findIndex((column) => column.key === targetKey);

			if (draggedIndex < 0 || targetIndex < 0) {
				return prev;
			}

			const next = [...prev];
			const [draggedItem] = next.splice(draggedIndex, 1);
			next.splice(targetIndex, 0, draggedItem);
			return next;
		});
	};

	const handleDragEnd = () => {
		setDraggedKey(null);
	};

	const handleApply = async () => {
		try {
			const patchColumns = preparePatchColumnPreferences(draftColumns);
			await onApply(patchColumns);
			setOpen(false);
		} catch (err: any) {
			toastText(
				err?.message || 'Invalid column preferences',
				'error'
			);
		}
	};

	const content = (
		<div className={styles['column-selector']}>
			<div className={styles['column-selector__list']}>
				{popoverList.map((item) => {
					if (item.type === 'fixed') {
						return (
							<div
								key={item.key}
								className={`${styles['column-selector__item']} ${styles['column-selector__item--fixed']}`}
							>
								<HolderOutlined
									className={`${styles['column-selector__drag-handle']} ${styles['column-selector__drag-handle--disabled']}`}
								/>
								<Checkbox checked disabled />
								<span className={styles['column-selector__label']}>
									{COLUMN_LABELS[item.key]}
								</span>
							</div>
						);
					}

					return (
						<div
							key={item.key}
							className={`${styles['column-selector__item']} ${
								draggedKey === item.key
									? styles['column-selector__item--dragging']
									: ''
							}`}
							draggable
							onDragStart={() => handleDragStart(item.key)}
							onDragOver={(event) => handleDragOver(event, item.key)}
							onDragEnd={handleDragEnd}
						>
							<HolderOutlined
								className={styles['column-selector__drag-handle']}
							/>
							<Checkbox
								checked={item.visible}
								onChange={(event) =>
									handleVisibilityChange(item.key, event.target.checked)
								}
							/>
							<span className={styles['column-selector__label']}>
								{COLUMN_LABELS[item.key]}
							</span>
						</div>
					);
				})}
			</div>
			<div className={styles['column-selector__footer']}>
				<Button
					type="primary"
					className="btn-blue"
					loading={isApplying}
					onClick={handleApply}
				>
					Apply
				</Button>
			</div>
		</div>
	);

	const isTriggerDisabled = isApplying;

	return (
		<Popover
			content={content}
			trigger="click"
			placement="bottomRight"
			open={open}
			onOpenChange={(visible) => {
				if (!isTriggerDisabled) {
					setOpen(visible);
				}
			}}
			overlayStyle={{ width: 260 }}
		>
			<Tooltip title="Customize column visibility and order for the payroll overview table">
				<span className={styles['column-selector__trigger']}>
					<FaSlidersH
						style={{
							fontSize: '20px',
							cursor: isTriggerDisabled ? 'not-allowed' : 'pointer',
							color: '#584495',
							opacity: isTriggerDisabled ? 0.6 : 1,
							transition: 'transform 0.3s',
						}}
					/>
				</span>
			</Tooltip>
		</Popover>
	);
};

export default ColumnSelector;
